import { useEffect, useState, JSX } from "react";
import { useTranslation } from "react-i18next";
import { Elements } from "@stripe/react-stripe-js";
import { StripeElementLocale } from "@stripe/stripe-js";
import { useLocale } from "@/hooks/useLocale";

import { LoadingCircle } from "@industriousoffice/react-components";

import { ZuoraPaymentMethodTypes } from "@/api/opb/opbTypes";
import { loadStripeWithConfig } from "@/utils/stripe";

import { opbApi } from "../../../api/opb/opbApi";

import { StripeSupportedCurrency } from "../types";
import StripeAddPaymentMethodForm from "./StripeAddPaymentMethodForm";

type StripePaymentMethodDrawerFormProps = {
  accountId: string | null;
  paymentType: ZuoraPaymentMethodTypes;
  currencyIsoCode?: string;
  isFirstPaymentMethod?: boolean;
  isDefaultPaymentChecked: boolean;
  setIsDefaultPaymentChecked: (checked: boolean) => void;
  paymentMethodInformation?: () => JSX.Element;
  submitLabel?: string;
};

export default function StripePaymentMethodDrawerForm({
  accountId,
  paymentType,
  currencyIsoCode,
  isFirstPaymentMethod,
  isDefaultPaymentChecked,
  setIsDefaultPaymentChecked,
  submitLabel,
  paymentMethodInformation,
}: StripePaymentMethodDrawerFormProps) {
  const { t } = useTranslation("paymentMethods");
  const [locale] = useLocale();
  const base = import.meta.env.VITE_CONTENT_ROOT;

  const [stripeInstance, setStripeInstance] = useState<Awaited<
    ReturnType<typeof loadStripeWithConfig>
  > | null>(null);
  const [paymentIntent, setPaymentIntent] = useState<Awaited<
    ReturnType<typeof opbApi.createAccountSetupIntent>
  > | null>(null);

  const [paymentIntentError, setPaymentIntentError] = useState<boolean>(false);

  const isLoadingPaymentForm =
    paymentIntent?.clientSecret == null && paymentIntentError == null;
  const currency =
    (currencyIsoCode?.toLowerCase() as StripeSupportedCurrency) ||
    StripeSupportedCurrency.Usd;

  const _paymentType = paymentType.toLowerCase() as ZuoraPaymentMethodTypes;

  useEffect(() => {
    const createSetupIntentAsync = async () => {
      if (accountId == null) {
        return;
      }

      setStripeInstance(await loadStripeWithConfig(currency));
      const response = await opbApi.createAccountSetupIntent({
        accountId,
        currency,
        paymentType: _paymentType,
      });
      if (response) {
        setPaymentIntent(response);
      } else {
        setPaymentIntentError(true);
      }
    };

    createSetupIntentAsync();
  }, [accountId, currency]);

  return (
    <>
      {paymentIntent?.clientSecret && stripeInstance && (
        <Elements
          stripe={stripeInstance}
          options={{
            clientSecret: paymentIntent.clientSecret,
            locale: locale as StripeElementLocale,
          }}
        >
          <StripeAddPaymentMethodForm
            returnUrl={`${window.location.origin}${base}/payment-methods/${accountId}`}
            clientSecret={paymentIntent.clientSecret}
            isDefaultPaymentChecked={isDefaultPaymentChecked}
            isFirstPaymentMethod={isFirstPaymentMethod}
            paymentMethodInformation={paymentMethodInformation}
            setIsDefaultPaymentChecked={setIsDefaultPaymentChecked}
            submitLabel={submitLabel}
          />
        </Elements>
      )}

      {isLoadingPaymentForm && <LoadingCircle spinnerColor="#013e3f" />}

      {paymentIntentError && (
        <div className="p-3 mt-2 bg-red-200 rounded-sm">
          {t("Sorry, something went wrong.")}
        </div>
      )}
    </>
  );
}
